import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import RiskBadge from "@/components/RiskBadge";
import { StatusPill, PriorityPill } from "./Cases";
import { toast } from "sonner";
import { ArrowLeft, Download, Trash2, UserPlus, FileText } from "lucide-react";

const STATUSES = ["Open", "Watching", "Escalated", "Closed"];

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = user?.role === "analyst";
  const [c, setC] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: caseData }, { data: notesData }] = await Promise.all([
        api.get(`/cases/${id}`),
        api.get(`/cases/${id}/notes`),
      ]);
      setC(caseData);
      setNotes(notesData);
    } catch (err) {
      toast.error("Could not load case");
      navigate("/cases");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    if (!readOnly) api.get("/users/directory").then(({ data }) => setDirectory(data)).catch(() => {});
  }, [readOnly]);

  const changeStatus = async (status) => {
    try { await api.patch(`/cases/${id}`, { status }); setC((p) => ({ ...p, status })); toast.success(`Status → ${status}`); }
    catch { toast.error("Could not update status"); }
  };
  const changePriority = async (priority) => {
    try { await api.patch(`/cases/${id}`, { priority }); setC((p) => ({ ...p, priority })); }
    catch { toast.error("Could not update priority"); }
  };
  const removeAd = async (adId) => {
    try { await api.delete(`/cases/${id}/ads/${adId}`); toast.success("Removed"); fetchAll(); }
    catch { toast.error("Could not remove ad"); }
  };
  const addCollaborator = async (uid) => {
    if (!uid) return;
    try { await api.post(`/cases/${id}/collaborators`, { user_id: uid }); toast.success("Collaborator added"); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not add collaborator"); }
  };
  const submitNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    try { await api.post(`/cases/${id}/notes`, { body: noteText }); setNoteText(""); toast.success("Note added"); fetchAll(); }
    catch { toast.error("Could not save note"); }
  };
  const downloadDossier = async () => {
    try {
      const res = await api.get(`/cases/${id}/dossier`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `MUKTI_case_${id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Dossier downloaded");
    } catch { toast.error("Could not generate dossier"); }
  };

  if (loading || !c) return <div className="p-6 text-sm text-slate-500">Loading case…</div>;

  const memberIds = new Set([c.owner_id, ...(c.collaborators || [])]);
  const addable = directory.filter((d) => !memberIds.has(d.id));

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl" data-testid="case-detail-page">
      <button onClick={() => navigate("/cases")} data-testid="case-back-btn"
        className="text-xs text-slate-500 hover:text-slate-900 font-mono uppercase tracking-wider inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" /> All cases
      </button>

      <header className="flex flex-col lg:flex-row gap-4 lg:items-start justify-between">
        <div className="space-y-2 min-w-0">
          <div className="flex items-center gap-2"><StatusPill status={c.status} /><PriorityPill priority={c.priority} /></div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl">{c.title}</h1>
          {c.description && <p className="text-sm text-slate-600 max-w-2xl whitespace-pre-wrap">{c.description}</p>}
        </div>
        <Button data-testid="download-case-dossier" onClick={downloadDossier} variant="outline" className="gap-1.5 border-slate-300">
          <Download className="h-4 w-4" /> Download dossier
        </Button>
      </header>

      <div className="grid lg:grid-cols-[1fr_300px] gap-6">
        <div className="space-y-6">
          <section className="border border-slate-200 bg-white rounded-md p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-lg tracking-tight">Linked advertisements ({c.ads?.length || 0})</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {(c.ads || []).map((a) => (
                <div key={a.id} data-testid={`case-ad-${a.id}`} className="flex items-center justify-between gap-3 py-2.5">
                  <Link to={`/ads/${a.id}`} className="min-w-0 flex-1 hover:bg-slate-50 -mx-2 px-2 py-1 rounded-md">
                    <div className="text-sm truncate">{a.title}</div>
                    <div className="text-xs text-slate-500 font-mono truncate">{(a.phones || []).join(" · ") || a.locality || "—"}</div>
                  </Link>
                  <RiskBadge level={a.risk?.level} score={a.risk?.score} />
                  {!readOnly && (
                    <button data-testid={`remove-ad-${a.id}`} onClick={() => removeAd(a.id)} className="text-slate-400 hover:text-red-600 p-1" aria-label="Remove">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              {(c.ads || []).length === 0 && (
                <div className="text-xs text-slate-400 py-3">No advertisements linked yet. Open an advertisement and use “Add to case”.</div>
              )}
            </div>
          </section>

          <section className="border border-slate-200 bg-white rounded-md p-5">
            <h2 className="font-display font-bold text-lg tracking-tight mb-3 flex items-center gap-2"><FileText className="h-4 w-4" /> Case notes</h2>
            {!readOnly && (
              <form onSubmit={submitNote} className="space-y-2 mb-4">
                <Textarea data-testid="case-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add an investigative note…" rows={3} />
                <Button data-testid="case-note-submit" type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700">Add note</Button>
              </form>
            )}
            <ul className="space-y-3">
              {notes.map((n) => (
                <li key={n.id} className="border border-slate-100 rounded-md p-3 bg-slate-50">
                  <div className="text-xs text-slate-500 font-mono mb-1">{n.author_name} · {(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  <div className="text-sm whitespace-pre-wrap">{n.body}</div>
                </li>
              ))}
              {notes.length === 0 && <li className="text-xs text-slate-400">No notes yet.</li>}
            </ul>
          </section>
        </div>

        <aside className="space-y-6">
          {!readOnly && (
            <section className="border border-slate-200 bg-white rounded-md p-5 space-y-3">
              <h2 className="font-display font-bold text-lg tracking-tight">Workflow</h2>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-1">Status</div>
                <select data-testid="case-status-select" value={c.status} onChange={(e) => changeStatus(e.target.value)}
                  className="w-full border border-slate-300 rounded-md text-sm px-2 py-2">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-1">Priority</div>
                <select data-testid="case-priority-change" value={c.priority} onChange={(e) => changePriority(e.target.value)}
                  className="w-full border border-slate-300 rounded-md text-sm px-2 py-2">
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
              </div>
            </section>
          )}

          <section className="border border-slate-200 bg-white rounded-md p-5 space-y-3">
            <h2 className="font-display font-bold text-lg tracking-tight flex items-center gap-2"><UserPlus className="h-4 w-4" /> Team</h2>
            <div className="text-xs text-slate-500 font-mono">Owner: {c.owner_name}</div>
            <div className="flex flex-wrap gap-1.5">
              {(c.collaborator_names || []).map((n, i) => (
                <span key={`${n}-${i}`} className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200">{n}</span>
              ))}
              {(c.collaborator_names || []).length === 0 && <span className="text-xs text-slate-400">No collaborators yet.</span>}
            </div>
            {!readOnly && addable.length > 0 && (
              <select data-testid="add-collaborator-select" defaultValue="" onChange={(e) => { addCollaborator(e.target.value); e.target.value = ""; }}
                className="w-full border border-slate-300 rounded-md text-sm px-2 py-2">
                <option value="" disabled>Add collaborator…</option>
                {addable.map((d) => <option key={d.id} value={d.id}>{d.name} ({d.role})</option>)}
              </select>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}
